import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Seo from '../components/Seo';
import RichContent from '../components/RichContent';
import RelatedPosts from '../components/RelatedPosts';
import Faqs from '../components/Faqs';
import { brand } from '../config/brand';
import { api } from '../lib/api';

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: post, isLoading, error } = useQuery({
    queryKey: ['blog', slug],
    queryFn: () => api.getBlog(slug!),
    enabled: !!slug,
  });

  if (isLoading) return <div className="container-page py-20 text-center text-slate-500">Loading…</div>;
  if (error || !post) {
    return (
      <div className="container-page py-20 text-center">
        <h1 className="font-display text-2xl font-bold">Post not found</h1>
        <Link to="/blog" className="btn-primary mt-6 inline-flex">Back to Insights</Link>
      </div>
    );
  }

  const hasHindi = !!post.bodyHtmlHi?.trim();
  const isHi = hasHindi && searchParams.get('lang') === 'hi';
  const title = (isHi && post.titleHi) || post.title;
  const excerpt = (isHi && post.excerptHi) || post.excerpt;
  const bodyHtml = isHi ? post.bodyHtmlHi : post.bodyHtml;
  const hasRichBody = !!bodyHtml?.trim();
  const faqs = (isHi && post.faqsHi?.length ? post.faqsHi : post.faqs) ?? [];
  const enUrl = post.canonicalUrl || `${brand.siteUrl}/blog/${post.rowKey}`;
  const hiUrl = `${brand.siteUrl}/blog/${post.rowKey}?lang=hi`;
  const setLang = (lang: 'en' | 'hi') => {
    const next = new URLSearchParams(searchParams);
    if (lang === 'hi') next.set('lang', 'hi');
    else next.delete('lang');
    setSearchParams(next, { replace: true });
  };

  // Compose JSON-LD: an Article entity + an optional FAQPage when the post
  // carries FAQs. Emitting FAQPage makes the post eligible for answer-engine
  // (AEO) rich results and gives LLMs clean question/answer pairs to cite.
  const jsonLd: Array<Record<string, unknown>> = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: title,
      inLanguage: isHi ? 'hi-IN' : 'en-IN',
      image: post.coverImageUrl ? [post.coverImageUrl] : undefined,
      datePublished: post.publishedAt || post.updatedAt,
      dateModified: post.updatedAt,
      author: post.author ? { '@type': 'Person', name: post.author } : undefined,
      publisher: {
        '@type': 'Organization',
        name: brand.name,
        logo: { '@type': 'ImageObject', url: brand.ogImage },
      },
      description: excerpt || undefined,
      ...(hasHindi
        ? { mainEntityOfPage: { '@type': 'WebPage', '@id': isHi ? hiUrl : enUrl } }
        : post.canonicalUrl
          ? { mainEntityOfPage: { '@type': 'WebPage', '@id': post.canonicalUrl } }
          : {}),
    },
  ];
  if (faqs.length > 0) {
    jsonLd.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
  }

  return (
    <>
      <Seo
        title={title}
        description={excerpt}
        image={post.coverImageUrl}
        seoTitle={isHi ? post.seoTitleHi : post.seoTitle}
        seoDescription={isHi ? post.seoDescriptionHi : post.seoDescription}
        seoImageUrl={post.seoImageUrl}
        canonicalUrl={hasHindi ? (isHi ? hiUrl : enUrl) : post.canonicalUrl}
        type="article"
        jsonLd={jsonLd}
        lang={isHi ? 'hi' : 'en'}
        alternates={
          hasHindi
            ? [
                { hrefLang: 'en-IN', href: enUrl },
                { hrefLang: 'hi-IN', href: hiUrl },
                { hrefLang: 'x-default', href: enUrl },
              ]
            : undefined
        }
      />

      <article lang={isHi ? 'hi' : 'en'} className="container-page mx-auto max-w-3xl py-12">
        <div className="flex items-center justify-between gap-4">
          <Link to="/blog" className="text-sm text-brand-600 hover:underline">← {isHi ? 'लेख' : 'Insights'}</Link>
          {hasHindi && (
            <div role="group" aria-label="Language" className="inline-flex rounded-full border border-slate-200 p-0.5 text-sm">
              <button
                type="button"
                onClick={() => setLang('en')}
                aria-pressed={!isHi}
                className={`rounded-full px-3 py-1 ${!isHi ? 'bg-brand-600 text-white' : 'text-slate-600'}`}
              >
                English
              </button>
              <button
                type="button"
                lang="hi"
                onClick={() => setLang('hi')}
                aria-pressed={isHi}
                className={`rounded-full px-3 py-1 ${isHi ? 'bg-brand-600 text-white' : 'text-slate-600'}`}
              >
                हिंदी
              </button>
            </div>
          )}
        </div>

        {post.coverImageUrl && (
          <img src={post.coverImageUrl} alt={title} className="mt-6 aspect-video w-full rounded-xl object-cover" />
        )}
        <h1 className="mt-6 font-display text-4xl font-bold">{title}</h1>
        <div className="mt-3 flex items-center gap-3 text-sm text-slate-500">
          {post.author && <span>{post.author}</span>}
          {post.publishedAt && <span>· {new Date(post.publishedAt).toLocaleDateString()}</span>}
        </div>

        {hasRichBody ? (
          <div className="mt-8">
            <RichContent html={bodyHtml!} />
          </div>
        ) : (
          <div className="prose prose-slate prose-headings:font-display prose-a:text-brand-600 mt-8 max-w-none">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.bodyMarkdown}</ReactMarkdown>
          </div>
        )}

        {faqs.length > 0 && (
          <section className="mt-14 border-t border-slate-100 pt-10">
            <h2 className="font-display text-2xl font-bold text-slate-900">
              {isHi ? 'अक्सर पूछे जाने वाले सवाल' : 'Frequently asked questions'}
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              {isHi ? 'खरीदारों और हाउसकीपिंग टीमों के सबसे आम सवालों के सीधे जवाब।' : 'Straight answers to what parents and buyers ask most.'}
            </p>
            <div className="mt-6">
              <Faqs items={faqs} />
            </div>
          </section>
        )}
      </article>

      <RelatedPosts slugs={post.relatedSlugs ?? []} />
    </>
  );
}
